import { postAppBridgeMessage, getBridgeOptions } from '../../../bridge';
import API from '../../../api';
import { isAvailable } from '../../utils';
import type {
  ServiceHubAddListener,
  ServiceHubExecuteAction,
  ServiceHubAction,
  ServiceHubQueryAllSkills,
  ServiceHubAllServices,
} from '../core/types';
import { NativeAPIResponseMessage } from '@zoom/hybrid-jsbridge';

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.addEventListener
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
let addEventListenerPromise: Promise<ServiceHubAddListener> | null = null;
function addEventListener(): Promise<ServiceHubAddListener> {
  const bridgeOptions = getBridgeOptions();
  if (bridgeOptions?.config?.serviceHubSkipAddEventListener) {
    console.log('skip serviceHubSkipAddEventListener');
    addEventListenerPromise = Promise.resolve({} as ServiceHubAddListener);
  } else {
    if (!addEventListenerPromise) {
      addEventListenerPromise = isAvailable('serviceHub.addEventListener').then(
        () => {
          return postAppBridgeMessage(API.serviceHubAddEventListener, {});
        }
      );
    }
  }
  return addEventListenerPromise;
}

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.executeAction
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function executeAction<T>(
  action: ServiceHubAction<T>,
  options?: { timeout?: number }
): Promise<NativeAPIResponseMessage<ServiceHubExecuteAction>> {
  return addEventListener().then(async () => {
    return isAvailable('serviceHub.executeAction').then(() => {
      return postAppBridgeMessage(API.serviceHubExecuteAction, action, options);
    });
  });
}

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.queryAllSkills
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function queryAllSkills(): Promise<
  NativeAPIResponseMessage<ServiceHubQueryAllSkills>
> {
  return addEventListener().then(async () => {
    return isAvailable('serviceHub.queryAllSkills').then(() => {
      return postAppBridgeMessage(API.serviceHubQueryAllSkills, {});
    });
  });
}

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.getAllServices
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function getAllServices(): Promise<
  NativeAPIResponseMessage<ServiceHubAllServices>
> {
  return addEventListener().then(async () => {
    return isAvailable('serviceHub.getAllServices').then(() => {
      return postAppBridgeMessage(API.serviceHubGetAllServices, {});
    });
  });
}

export default {
  getAllServices,
  addEventListener,
  executeAction,
  queryAllSkills,
};
